import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { toast } from 'react-hot-toast';
import { apiService } from '../services/api';
import { UserConversation, ConversationMessage } from '../types';

interface UserConversationState {
  conversations: UserConversation[];
  currentConversation: UserConversation | null;
  messages: ConversationMessage[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
}

interface UserConversationContextType extends UserConversationState {
  loadConversations: (options?: { page?: number; limit?: number; search?: string }) => Promise<void>;
  selectConversation: (conversationId: string) => Promise<void>;
  createConversationFromChat: (chatId: string, title?: string) => Promise<void>;
  updateConversation: (conversationId: string, updates: Partial<UserConversation>) => Promise<void>;
  deleteConversation: (conversationId: string) => Promise<void>;
  clearCurrentConversation: () => void;
  clearError: () => void;
}

type UserConversationAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_CONVERSATIONS'; payload: UserConversation[] }
  | { type: 'SET_CURRENT_CONVERSATION'; payload: UserConversation | null }
  | { type: 'SET_MESSAGES'; payload: ConversationMessage[] }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_HAS_MORE'; payload: boolean }
  | { type: 'SET_PAGINATION'; payload: { page: number; limit: number; total: number } }
  | { type: 'ADD_CONVERSATION'; payload: UserConversation }
  | { type: 'UPDATE_CONVERSATION'; payload: { id: string; updates: Partial<UserConversation> } }
  | { type: 'REMOVE_CONVERSATION'; payload: string };

const initialState: UserConversationState = {
  conversations: [],
  currentConversation: null,
  messages: [],
  loading: false,
  error: null,
  hasMore: false,
  pagination: {
    page: 1,
    limit: 20,
    total: 0
  }
};

const userConversationReducer = (state: UserConversationState, action: UserConversationAction): UserConversationState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_CONVERSATIONS':
      return { ...state, conversations: action.payload };
    case 'SET_CURRENT_CONVERSATION':
      return { ...state, currentConversation: action.payload };
    case 'SET_MESSAGES':
      return { ...state, messages: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_HAS_MORE':
      return { ...state, hasMore: action.payload };
    case 'SET_PAGINATION':
      return { ...state, pagination: action.payload };
    case 'ADD_CONVERSATION':
      return { ...state, conversations: [action.payload, ...state.conversations] };
    case 'UPDATE_CONVERSATION':
      return {
        ...state,
        conversations: state.conversations.map(conv =>
          conv._id === action.payload.id ? { ...conv, ...action.payload.updates } : conv
        ),
        currentConversation: state.currentConversation?._id === action.payload.id
          ? { ...state.currentConversation, ...action.payload.updates }
          : state.currentConversation
      };
    case 'REMOVE_CONVERSATION':
      return {
        ...state,
        conversations: state.conversations.filter(conv => conv._id !== action.payload),
        currentConversation: state.currentConversation?._id === action.payload ? null : state.currentConversation
      };
    default:
      return state;
  }
};

const UserConversationContext = createContext<UserConversationContextType | undefined>(undefined);

export const useUserConversation = (): UserConversationContextType => {
  const context = useContext(UserConversationContext);
  if (!context) {
    throw new Error('useUserConversation must be used within a UserConversationProvider');
  }
  return context;
};

interface UserConversationProviderProps {
  children: ReactNode;
}

export const UserConversationProvider: React.FC<UserConversationProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(userConversationReducer, initialState);

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
  }, []);

  const loadConversations = async (options?: { page?: number; limit?: number; search?: string }) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      const response = await apiService.getUserConversations({
        page: options?.page || 1,
        limit: options?.limit || 20,
        search: options?.search
      });

      if (response.success && response.data) {
        dispatch({ type: 'SET_CONVERSATIONS', payload: response.data.conversations });
        dispatch({ type: 'SET_PAGINATION', payload: {
          page: response.data.pagination.current,
          limit: response.data.pagination.limit,
          total: response.data.pagination.total
        }});
        dispatch({ type: 'SET_HAS_MORE', payload: response.data.conversations.length === (options?.limit || 20) });
      } else {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to load conversations' });
      }
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load conversations' });
      toast.error('Failed to load conversations');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const selectConversation = async (conversationId: string): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      const response = await apiService.getUserConversation(conversationId);

      if (response.success && response.data) {
        dispatch({ type: 'SET_CURRENT_CONVERSATION', payload: response.data.conversation });
        dispatch({ type: 'SET_MESSAGES', payload: response.data.conversation.messages });
      } else {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to load conversation' });
      }
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load conversation' });
      toast.error('Failed to load conversation');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const createConversationFromChat = async (chatId: string, title?: string): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      const response = await apiService.createUserConversation({
        chatId,
        title: title || 'New Conversation'
      });

      if (response.success && response.data) {
        dispatch({ type: 'ADD_CONVERSATION', payload: response.data });
        toast.success('Conversation created successfully');
      } else {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to create conversation' });
      }
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to create conversation' });
      toast.error('Failed to create conversation');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const updateConversation = async (conversationId: string, updates: Partial<UserConversation>): Promise<void> => {
    try {
      const response = await apiService.updateUserConversation(conversationId, updates);

      if (response.success && response.data) {
        dispatch({ type: 'UPDATE_CONVERSATION', payload: { id: conversationId, updates } });
        toast.success('Conversation updated successfully');
      } else {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to update conversation' });
      }
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to update conversation' });
      toast.error('Failed to update conversation');
    }
  };

  const deleteConversation = async (conversationId: string): Promise<void> => {
    try {
      const response = await apiService.deleteUserConversation(conversationId);

      if (response.success) {
        dispatch({ type: 'REMOVE_CONVERSATION', payload: conversationId });
        toast.success('Conversation deleted successfully');
      } else {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to delete conversation' });
      }
    } catch (error: any) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to delete conversation' });
      toast.error('Failed to delete conversation');
    }
  };

  const clearCurrentConversation = () => {
    dispatch({ type: 'SET_CURRENT_CONVERSATION', payload: null });
    dispatch({ type: 'SET_MESSAGES', payload: [] });
  };

  const clearError = () => {
    dispatch({ type: 'SET_ERROR', payload: null });
  };

  const value: UserConversationContextType = {
    ...state,
    loadConversations,
    selectConversation,
    createConversationFromChat,
    updateConversation,
    deleteConversation,
    clearCurrentConversation,
    clearError
  };

  return (
    <UserConversationContext.Provider value={value}>
      {children}
    </UserConversationContext.Provider>
  );
};
